import {useQueryClient} from '@tanstack/react-query';
import React, {ReactNode, createContext, useContext, useEffect, useState} from 'react';

import {distributorT} from '@/lib/types/distributor.types';
import {marketT} from '@/lib/types/market.types';
import {rateInsertWithProductBrandT} from '@/lib/types/rateSheets.types';
import {BasicDistributorT} from '@/lib/types/user.types';
import {FlattenT} from '@/lib/types/utility.types';

import {SupabaseInstance} from '@/lib/utils/supabaseClass';

import {useDataQuery, useItemQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

export type preparedRateT = Partial<rateInsertWithProductBrandT>;

type rateSheetContextT = {
  error: string;
  handleSubmitRateSheet: () => void;
  isSubmitting: boolean;
  acknowledged: boolean;
  setAcknowledged: (acknowledged: boolean) => void;
  setAcknowledging: (acknowledged: boolean) => void;
  acknowledging: boolean;
  rates: FlattenT<preparedRateT & {error: boolean; errorMessage: string}>[];
  selectedDistributor: BasicDistributorT | distributorT | null;
  selectedMarket: marketT | null;
  validateRateSheet: () => boolean;
  updateRate: (index: number, data: preparedRateT) => void;
  deleteRate: (index: number) => void;
  setSelectedDistributor: (distributor: distributorT | null) => void;
  setSelectedMarket: (market: marketT | null) => void;
  addRate: (data: preparedRateT) => void;
  title: string;
  setTitle: (title: string) => void;
  mode: 'view' | 'edit' | 'create';
  resetRates: () => void;
};

const RateSheetContext = createContext<rateSheetContextT | undefined>(undefined);

export const useCreateRateSheet = () => {
  const context = useContext(RateSheetContext);
  if (!context) {
    throw new Error('useCreateRateSheet must be used within a CreateRateSheetProvider');
  }
  return context;
};

interface CreateRateSheetProviderProps {
  children: ReactNode;
  rateSheetId?: string;
  mode?: 'view' | 'edit' | 'create';
}

/**
 * Provider for creating rate sheets
 */
export const CreateRateSheetProvider: React.FC<CreateRateSheetProviderProps> = ({
  children,
  rateSheetId,
  mode = 'create',
}) => {
  const queryClient = useQueryClient();
  const {userAccounts} = useAuth();
  const {toast} = useNotifications();

  // State
  const [selectedDistributor, setSelectedDistributor] = useState<distributorT | null>(null);
  const [removedRates, setRemovedRates] = useState<{rate_id: number; removed: boolean}[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<marketT | null>(null);
  const [error, setError] = useState('');
  const [rates, setRates] = useState<FlattenT<preparedRateT & {error: boolean; errorMessage: string}>[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [title, setTitle] = useState('');

  /**
   * Add a rate
   * @param data - The data to add to the rate sheet
   */
  const addRate = (data: preparedRateT) => {
    const newRate = {
      ...data,
      error: false,
      errorMessage: '',
    };
    setRates([...rates, newRate]);
  };

  /**
   * Update a rate
   * @param index - The index of the rate to update
   * @param data - The data to update the rate with
   */
  const updateRate = (index: number, data: preparedRateT) => {
    const newRates = [...rates];
    newRates[index] = {...newRates[index], ...data};
    setRates(newRates);
  };

  /**
   * Delete a rate
   * @param index - The index of the rate to delete
   */
  const deleteRate = (index: number) => {
    const rate = rates[index];
    // If the rate has an ID (existing rate), mark it for removal
    if (rate.rate_id) {
      setRemovedRates([...removedRates, {rate_id: rate.rate_id, removed: true}]);
    }
    const newRates = rates.filter((_, i) => i !== index);
    setRates(newRates);
  };

  /**
   * Validate rate sheet positions
   */
  const validateRateSheet = () => {
    if (rates.length === 0) return false;

    const newRates = rates.map(rate => {
      let errorMessage = '';
      if (!rate.fk_product_id) errorMessage = 'Product is required';
      else if (!rate.fk_brand_id) errorMessage = 'Brand is required';
      else if (rate.wholesale_price! <= 0 || isNaN(rate.wholesale_price!))
        errorMessage = 'Wholesale price must be a positive number';
      else if (rate.tnc_price! <= 0 || isNaN(rate.tnc_price!)) errorMessage = 'TNC price must be a positive number';
      else if (rate.factored_price! <= 0 || isNaN(rate.factored_price!))
        errorMessage = 'Factored price must be a positive number';
      else if (rate.min_quantity! <= 0 || isNaN(rate.min_quantity!))
        errorMessage = 'Min quantity must be a positive number';

      return {
        ...rate,
        error: !!errorMessage,
        errorMessage,
      };
    });

    setRates(newRates);
    return newRates.every(rate => !rate.error);
  };

  /**
   * Helper function to invalidate rate sheets cache
   */
  const invalidateRateSheets = async () => {
    try {
      await queryClient.invalidateQueries({queryKey: ['Rate_Sheets']});
      await queryClient.refetchQueries({queryKey: ['Rate_Sheets']});
    } catch (error) {
      console.error('Error invalidating rate sheets:', error);
    }
  };

  /**
   * Submit rate sheet
   */
  const handleSubmitRateSheet = async () => {
    if (!selectedDistributor || !selectedMarket) {
      throw new Error('Distributor or market not selected');
    }
    const {data: supabaseUser} = await SupabaseInstance.supabase.auth.getUser();
    if (!userAccounts.distributors || !supabaseUser?.user) {
      throw new Error('User not found');
    }
    if (rates.length === 0) {
      throw new Error('No rates to submit');
    }

    setIsSubmitting(true);
    const isValid = validateRateSheet();

    if (!isValid) {
      console.error('Invalid positions');
      rates.forEach(rate => {
        if (rate.errorMessage) console.error(rate.errorMessage);
      });
      setIsSubmitting(false);
      return;
    }

    let rateSheetName = title.trim();
    if (rateSheetName === '') {
      rateSheetName = `${selectedDistributor.distributor_name}-${selectedMarket.market_name}-${Math.floor(Math.random() * 900 + 100)}`;
    }

    try {
      if (mode === 'edit') {
        await handleUpdateRateSheet(supabaseUser.user.id, rateSheetName);
      } else {
        const rateSheet = {
          date_created: new Date().toISOString(),
          date_modified: new Date().toISOString(),
          modified_by: supabaseUser.user.id,
          created_by: supabaseUser.user.id,
          fk_distributor_id: selectedDistributor?.distributor_id,
          fk_market_id: selectedMarket?.market_id,
          rate_sheet_name: rateSheetName,
        };

        const {data: rateSheetData, error: rateSheetError} = await SupabaseInstance.supabase
          .from('Rate_Sheets')
          .insert(rateSheet)
          .select()
          .single();

        if (rateSheetError || !rateSheetData) throw rateSheetError;

        // Prepare all rates for bulk insertion
        const ratesToInsert = rates.map(rate => ({
          fk_rate_sheet_id: rateSheetData.rate_sheet_id,
          created_by: supabaseUser.user.id,
          date_created: new Date().toISOString(),
          date_modified: new Date().toISOString(),
          fk_brand_id: rate.fk_brand_id!,
          fk_product_id: rate.fk_product_id!,
          wholesale_price: rate.wholesale_price!,
          tnc_price: rate.tnc_price!,
          factored_price: rate.factored_price!,
          msrp: rate.msrp === 0 ? null : rate.msrp,
          min_quantity: rate.min_quantity,
          modified_by: supabaseUser.user.id,
          fk_market_id: selectedMarket?.market_id,
        }));

        // Insert rates
        const {error: ratesError} = await SupabaseInstance.supabase.from('Rates').insert(ratesToInsert);

        if (ratesError) {
          // If rates insertion fails, rollback the rate sheet
          await rollbackRateSheet(rateSheetData.rate_sheet_id);
          throw ratesError;
        }

        toast.success('Rate sheet created successfully');
        setAcknowledged(true);
      }
    } catch (error) {
      console.error('Error creating rate sheet:', error);
      setError('Error creating rate sheet');
      toast.error('Error creating rate sheet');
      throw error;
    } finally {
      setIsSubmitting(false);
      invalidateRateSheets();
    }
  };

  /**
   * handle updating rate sheet
   */
  const handleUpdateRateSheet = async (userId: string, title: string) => {
    if (!rateSheetId) {
      throw new Error('Rate sheet ID is required');
    }
    try {
      const currentTime = new Date().toISOString();

      // Seperate rates into existing and new
      const existingRates = rates.filter(
        rate => rate.rate_id && rate.fk_rate_sheet_id === parseInt(rateSheetId || '0')
      );
      const newRates = rates.filter(rate => !rate.rate_id || rate.fk_rate_sheet_id !== parseInt(rateSheetId));

      // Update existing rates
      if (existingRates.length > 0) {
        const ratesToUpdate = existingRates.map(rate => ({
          rate_id: rate.rate_id,
          fk_rate_sheet_id: parseInt(rateSheetId),
          fk_brand_id: rate.fk_brand_id!,
          fk_product_id: rate.fk_product_id!,
          wholesale_price: rate.wholesale_price!,
          tnc_price: rate.tnc_price!,
          msrp: rate.msrp === 0 ? null : rate.msrp,
          min_quantity: rate.min_quantity,
          date_created: rate.date_created,
          created_by: rate.created_by || userId,
          modified_by: userId,
          date_modified: currentTime,
        }));

        const {error: ratesUpdateError} = await SupabaseInstance.supabase.from('Rates').upsert(ratesToUpdate).select();
        if (ratesUpdateError) throw ratesUpdateError;
      }

      // Handle removed rates
      if (removedRates.length > 0) {
        const {error: ratesRemoveError} = await SupabaseInstance.supabase
          .from('Rates')
          .update({
            date_removed: currentTime,
            removed_by: userId,
          })
          .in(
            'rate_id',
            removedRates.map(rate => rate.rate_id)
          );
        if (ratesRemoveError) throw ratesRemoveError;
      }

      // Insert new rates
      if (newRates.length > 0) {
        const ratesToInsert = newRates.map(rate => ({
          fk_rate_sheet_id: parseInt(rateSheetId),
          created_by: userId,
          date_created: currentTime,
          date_modified: currentTime,
          fk_brand_id: rate.fk_brand_id!,
          fk_product_id: rate.fk_product_id!,
          wholesale_price: rate.wholesale_price!,
          tnc_price: rate.tnc_price!,
          msrp: rate.msrp === 0 ? null : rate.msrp,
          min_quantity: rate.min_quantity,
          modified_by: userId,
        }));

        const {error: ratesInsertError} = await SupabaseInstance.supabase.from('Rates').insert(ratesToInsert);
        if (ratesInsertError) throw ratesInsertError;
      }

      // Update the rate sheet
      const {error: rateSheetUpdateError} = await SupabaseInstance.supabase
        .from('Rate_Sheets')
        .update({date_modified: currentTime, modified_by: userId, rate_sheet_name: title})
        .eq('rate_sheet_id', parseInt(rateSheetId))
        .select();
      if (rateSheetUpdateError) throw rateSheetUpdateError;

      toast.success('Rate sheet updated successfully');
    } catch (error) {
      console.error('Error updating rate sheet:', error);
      setError('Error updating rate sheet');
      toast.error('Error updating rate sheet');
      throw error;
    }
  };

  /**
   * Rolls back a rate sheet and its associated rates
   * @param rateSheetId - The ID of the rate sheet to rollback
   */
  const rollbackRateSheet = async (rateSheetId: number) => {
    try {
      // Delete all rates associated with the rate sheet
      await SupabaseInstance.supabase.from('Rates').delete().match({fk_rate_sheet_id: rateSheetId});

      // Delete the rate sheet
      await SupabaseInstance.supabase.from('Rate_Sheets').delete().match({rate_sheet_id: rateSheetId});
    } catch (error) {
      console.error('Error rolling back rate sheet:', error);
    }
  };

  /**************************************************
   * Existing Rate Sheet
   **************************************************/
  const {data: existingRateSheet, refetch: refetchExistingRateSheet} = useItemQuery({
    table: 'Rate_Sheets',
    columns: ['*', ['Distributor', ['*']], ['Market', ['*']]],
    id: parseInt(rateSheetId || '0'),
    idColumn: 'rate_sheet_id',
    enabled: !!rateSheetId,
  });

  const {data: {data: existingRates} = {data: []}, refetch: refetchExistingRates} = useDataQuery({
    table: 'Rates',
    columns: ['*', ['Brand', ['*']], ['Product', ['*']]],
    filters: [
      {column: 'fk_rate_sheet_id', operator: 'eq', value: parseInt(rateSheetId || '0')},
      {column: 'date_removed', operator: 'is', value: null},
    ],
    enabled: !!rateSheetId,
  });

  // fetch existing rate sheet and rates
  useEffect(() => {
    if (existingRateSheet && existingRates && (mode === 'edit' || mode === 'view')) {
      // Set initial form values
      setTitle(existingRateSheet.rate_sheet_name);
      setSelectedDistributor(existingRateSheet.Distributor);
      setSelectedMarket(existingRateSheet.Market);

      // Transform existing rates to match our state structure
      const transformedRates = existingRates.map(rate => ({
        fk_product_id: rate.fk_product_id,
        fk_brand_id: rate.fk_brand_id,
        wholesale_price: rate.wholesale_price,
        tnc_price: rate.tnc_price,
        msrp: rate.msrp || null,
        factored_price: rate.factored_price,
        min_quantity: rate.min_quantity,
        Product: rate.Product,
        Brand: rate.Brand,
        rate_id: rate.rate_id,
        fk_rate_sheet_id: rate.fk_rate_sheet_id,
        created_by: rate.created_by,
        date_created: rate.date_created,
        error: false,
        errorMessage: '',
      }));

      setRates(transformedRates);
    }
  }, [existingRateSheet, existingRates, mode]);

  useEffect(() => {
    if (rateSheetId && mode === 'edit') {
      refetchExistingRateSheet();
      refetchExistingRates();
    }

    if (rateSheetId && mode === 'view') {
      refetchExistingRateSheet();
      refetchExistingRates();
      setAcknowledging(true);
    }
  }, [rateSheetId, mode, refetchExistingRateSheet, refetchExistingRates]);

  /************************
   * HELPERS
   *************************/
  const resetRates = () => {
    setRates([]);
  };

  const value = {
    error,
    isSubmitting,
    acknowledging,
    setAcknowledging,
    acknowledged,
    setAcknowledged,
    rates,
    selectedDistributor,
    selectedMarket,
    handleSubmitRateSheet,
    validateRateSheet,
    updateRate,
    deleteRate,
    setSelectedDistributor,
    setSelectedMarket,
    addRate,
    title,
    setTitle,
    mode,
    resetRates,
  };

  return <RateSheetContext.Provider value={value}>{children}</RateSheetContext.Provider>;
};
